import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useFormatTools } from "../hooks/useFormatTools";
import {
  alignShapes,
  centralizeOnSlide,
  arrangeCascade,
  alignCircle,
  relayoutCircle,
  addCircleShape,
  removeCircleShape,
  distributeShapes,
  adjustSpacing,
  stackShapes,
  duplicateShapes,
  dockShapes,
  fitToSlide,
  smartAlign,
  multiplyObject,
  sliceObject,
  swapShapes,
  resizeShapes,
  stretchShapes,
  deleteAllText,
  splitTextByParagraphs,
  mergeTextBoxes,
  sortTextAscending,
  sortTextDescending,
  removeLineBreaks,
  reorderShapesByText,
  replaceFont,
  setConnectorWeight,
  tableToTextExplode,
  tableToTextAsIs,
  splitTable,
  alignShapesInTable,
  captureSelectionGeometry,
  restoreShapeGeometry,
  type ShapeGeomSnapshot,
} from "../services/formatOps";
import { setTextMargins, zeroMargins } from "../services/marginOps";
import { applySingleLineSpacing, toggleWrapText } from "../services/textSpacingOps";
import {
  applyAllFormatting,
  applyFillColor,
  applyHeight,
  applyOutline,
  applyPosition,
  applyProportions,
  applyShapeStyle,
  applySize,
  applyTextFormat,
  applyWidth,
  clearCopiedStyle,
  copyStyle,
  getCopiedStyle,
  onCopiedStyleChange,
} from "../services/copyFormatOps";
import type {
  AlignEdge,
  AlignInTableConfig,
  CircleState,
  CopiedStyle,
  MarginPreset,
  StretchOp,
} from "../../../types";
import { GridConfigDialog, type GridMode } from "./GridConfigDialog";
import { TextConfigPanel, type TextPanelMode } from "./TextConfigPanel";
import { MarginConfigPanel } from "./MarginConfigPanel";
import { AlignCirclePanel } from "./AlignCirclePanel";
import { TableSplitPanel } from "./TableSplitPanel";
import { AlignInTablePanel } from "./AlignInTablePanel";
import styles from "./FormatTools.module.css";

interface ToolButton {
  key: string;
  // The catalog's `format-${actionName}` id suffix. Defaults to `key` when
  // omitted — only alignBtn/stretchBtn need to set this explicitly, since
  // their `key` carries an extra prefix ("align-left", "stretch-stretchLeft")
  // that doesn't match the catalog's action-name strings.
  cmdAction?: string;
  title: string;
  icon: ReactNode;
  action: () => Promise<void>;
  // When set, clicking opens the Multiply/Slice config dialog instead of
  // running `action` directly (which is a no-op for these buttons).
  dialog?: GridMode;
  // When set, clicking opens the matching Text Format input panel instead.
  textPanel?: TextPanelMode;
  // When set, clicking arranges in a circle and opens the Align Circle panel.
  circlePanel?: boolean;
  // When set, clicking opens the Table Split panel.
  tablePanel?: boolean;
  // When set, clicking opens the Align-in-Table panel.
  alignPanel?: boolean;
  // When set, clicking opens the Set Margin panel.
  marginPanel?: boolean;
}

/*
 * Buttons, order, titles and SVG glyphs are copied verbatim from the Deckorate
 * prototype `taskpane.html` (ALIGN + ARRANGE sections). Stroke/fill styling
 * comes from the CSS module (`.btn svg`), matching `alignTools.css`.
 */

function alignBtn(edge: AlignEdge, title: string, icon: ReactNode): ToolButton {
  const cmdAction = `align${edge.charAt(0).toUpperCase()}${edge.slice(1)}`;
  return { key: `align-${edge}`, cmdAction, title, icon, action: () => alignShapes(edge) };
}

const ALIGN_BUTTONS: ReadonlyArray<ToolButton> = [
  alignBtn(
    "left",
    "Align Left",
    <svg viewBox="0 0 20 20">
      <line x1="3" y1="2" x2="3" y2="18" />
      <rect x="3" y="4" width="12" height="4" rx="1" />
      <rect x="3" y="12" width="8" height="4" rx="1" />
    </svg>,
  ),
  alignBtn(
    "middle",
    "Align Middle",
    <svg viewBox="0 0 20 20">
      <line x1="10" y1="2" x2="10" y2="18" strokeDasharray="2,2" />
      <rect x="4" y="4" width="12" height="4" rx="1" />
      <rect x="6" y="12" width="8" height="4" rx="1" />
    </svg>,
  ),
  alignBtn(
    "right",
    "Align Right",
    <svg viewBox="0 0 20 20">
      <line x1="17" y1="2" x2="17" y2="18" />
      <rect x="5" y="4" width="12" height="4" rx="1" />
      <rect x="9" y="12" width="8" height="4" rx="1" />
    </svg>,
  ),
  alignBtn(
    "top",
    "Align Top",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="3" x2="18" y2="3" />
      <rect x="4" y="3" width="4" height="12" rx="1" />
      <rect x="12" y="3" width="4" height="8" rx="1" />
    </svg>,
  ),
  alignBtn(
    "center",
    "Align Center",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="10" x2="18" y2="10" strokeDasharray="2,2" />
      <rect x="4" y="4" width="4" height="12" rx="1" />
      <rect x="12" y="6" width="4" height="8" rx="1" />
    </svg>,
  ),
  alignBtn(
    "bottom",
    "Align Bottom",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="17" x2="18" y2="17" />
      <rect x="4" y="5" width="4" height="12" rx="1" />
      <rect x="12" y="9" width="4" height="8" rx="1" />
    </svg>,
  ),
];

const ARRANGE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "centralize",
    title: "Centralize on Slide",
    action: () => centralizeOnSlide(),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" strokeDasharray="2,2" />
        <rect x="6" y="6" width="8" height="8" rx="1" />
      </svg>
    ),
  },
  {
    key: "arrangeTopLeft",
    title: "Arrange Top-Left",
    action: () => arrangeCascade("topLeft"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="6" height="6" rx="1" />
        <rect x="5" y="5" width="6" height="6" rx="1" opacity="0.6" />
        <rect x="8" y="8" width="6" height="6" rx="1" opacity="0.3" />
      </svg>
    ),
  },
  {
    key: "arrangeTopRight",
    title: "Arrange Top-Right",
    action: () => arrangeCascade("topRight"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="12" y="2" width="6" height="6" rx="1" />
        <rect x="9" y="5" width="6" height="6" rx="1" opacity="0.6" />
        <rect x="6" y="8" width="6" height="6" rx="1" opacity="0.3" />
      </svg>
    ),
  },
  {
    key: "arrangeBottomLeft",
    title: "Arrange Bottom-Left",
    action: () => arrangeCascade("bottomLeft"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="8" y="6" width="6" height="6" rx="1" opacity="0.3" />
        <rect x="5" y="9" width="6" height="6" rx="1" opacity="0.6" />
        <rect x="2" y="12" width="6" height="6" rx="1" />
      </svg>
    ),
  },
  {
    key: "arrangeBottomRight",
    title: "Arrange Bottom-Right",
    action: () => arrangeCascade("bottomRight"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="6" width="6" height="6" rx="1" opacity="0.3" />
        <rect x="9" y="9" width="6" height="6" rx="1" opacity="0.6" />
        <rect x="12" y="12" width="6" height="6" rx="1" />
      </svg>
    ),
  },
];

const DISTRIBUTE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "distributeHorizontally",
    title: "Distribute Horizontally",
    action: () => distributeShapes("horizontal"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="6" width="4" height="8" rx="1" />
        <rect x="8" y="6" width="4" height="8" rx="1" />
        <rect x="14" y="6" width="4" height="8" rx="1" />
        <line x1="4" y1="3" x2="4" y2="5" opacity="0.4" />
        <line x1="10" y1="3" x2="10" y2="5" opacity="0.4" />
        <line x1="16" y1="3" x2="16" y2="5" opacity="0.4" />
      </svg>
    ),
  },
  {
    key: "distributeVertically",
    title: "Distribute Vertically",
    action: () => distributeShapes("vertical"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="2" width="8" height="4" rx="1" />
        <rect x="6" y="8" width="8" height="4" rx="1" />
        <rect x="6" y="14" width="8" height="4" rx="1" />
        <line x1="3" y1="4" x2="5" y2="4" opacity="0.4" />
        <line x1="3" y1="10" x2="5" y2="10" opacity="0.4" />
        <line x1="3" y1="16" x2="5" y2="16" opacity="0.4" />
      </svg>
    ),
  },
  {
    key: "increaseHorizSpace",
    title: "Increase Horizontal Space",
    action: () => adjustSpacing("horizontal", "increase"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="1" y="6" width="4" height="8" rx="1" />
        <rect x="15" y="6" width="4" height="8" rx="1" />
        <polyline points="9,8 7,10 9,12" />
        <polyline points="11,8 13,10 11,12" />
      </svg>
    ),
  },
  {
    key: "decreaseHorizSpace",
    title: "Decrease Horizontal Space",
    action: () => adjustSpacing("horizontal", "decrease"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="1" y="6" width="4" height="8" rx="1" />
        <rect x="15" y="6" width="4" height="8" rx="1" />
        <polyline points="7,8 9,10 7,12" />
        <polyline points="13,8 11,10 13,12" />
      </svg>
    ),
  },
  {
    key: "increaseVertSpace",
    title: "Increase Vertical Space",
    action: () => adjustSpacing("vertical", "increase"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="1" width="8" height="4" rx="1" />
        <rect x="6" y="15" width="8" height="4" rx="1" />
        <polyline points="8,9 10,7 12,9" />
        <polyline points="8,11 10,13 12,11" />
      </svg>
    ),
  },
  {
    key: "decreaseVertSpace",
    title: "Decrease Vertical Space",
    action: () => adjustSpacing("vertical", "decrease"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="1" width="8" height="4" rx="1" />
        <rect x="6" y="15" width="8" height="4" rx="1" />
        <polyline points="8,7 10,9 12,7" />
        <polyline points="8,13 10,11 12,13" />
      </svg>
    ),
  },
];

const DUPLICATE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "duplicateLeft",
    title: "Duplicate Left",
    action: () => duplicateShapes("left"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="10" y="5" width="8" height="10" rx="1" />
        <rect x="2" y="5" width="8" height="10" rx="1" strokeDasharray="2,2" />
        <line x1="8" y1="10" x2="4" y2="10" />
        <polyline points="6,8 4,10 6,12" />
      </svg>
    ),
  },
  {
    key: "duplicateRight",
    title: "Duplicate Right",
    action: () => duplicateShapes("right"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="5" width="8" height="10" rx="1" />
        <rect x="10" y="5" width="8" height="10" rx="1" strokeDasharray="2,2" />
        <line x1="12" y1="10" x2="16" y2="10" />
        <polyline points="14,8 16,10 14,12" />
      </svg>
    ),
  },
  {
    key: "duplicateUp",
    title: "Duplicate Up",
    action: () => duplicateShapes("up"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="5" y="10" width="10" height="8" rx="1" />
        <rect x="5" y="2" width="10" height="8" rx="1" strokeDasharray="2,2" />
        <line x1="10" y1="8" x2="10" y2="4" />
        <polyline points="8,6 10,4 12,6" />
      </svg>
    ),
  },
  {
    key: "duplicateDown",
    title: "Duplicate Down",
    action: () => duplicateShapes("down"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="5" y="2" width="10" height="8" rx="1" />
        <rect x="5" y="10" width="10" height="8" rx="1" strokeDasharray="2,2" />
        <line x1="10" y1="12" x2="10" y2="16" />
        <polyline points="8,14 10,16 12,14" />
      </svg>
    ),
  },
  {
    key: "stackHorizontal",
    title: "Stack Horizontal",
    action: () => stackShapes("horizontal"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="1" y="6" width="5" height="8" rx="1" />
        <rect x="7.5" y="6" width="5" height="8" rx="1" />
        <rect x="14" y="6" width="5" height="8" rx="1" />
      </svg>
    ),
  },
  {
    key: "stackVertical",
    title: "Stack Vertical",
    action: () => stackShapes("vertical"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="1" width="8" height="5" rx="1" />
        <rect x="6" y="7.5" width="8" height="5" rx="1" />
        <rect x="6" y="14" width="8" height="5" rx="1" />
      </svg>
    ),
  },
];

const DOCK_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "dockLeft",
    title: "Dock Left",
    action: () => dockShapes("left"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="8" y="4" width="8" height="12" rx="1" />
        <rect x="2" y="6" width="5" height="8" rx="1" strokeDasharray="2,2" />
        <polyline points="6,10 3,10" />
        <polyline points="5,8 3,10 5,12" />
      </svg>
    ),
  },
  {
    key: "dockRight",
    title: "Dock Right",
    action: () => dockShapes("right"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="4" y="4" width="8" height="12" rx="1" />
        <rect x="13" y="6" width="5" height="8" rx="1" strokeDasharray="2,2" />
        <polyline points="14,10 17,10" />
        <polyline points="15,8 17,10 15,12" />
      </svg>
    ),
  },
  {
    key: "dockTop",
    title: "Dock Top",
    action: () => dockShapes("top"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="4" y="8" width="12" height="8" rx="1" />
        <rect x="6" y="2" width="8" height="5" rx="1" strokeDasharray="2,2" />
        <polyline points="10,6 10,3" />
        <polyline points="8,5 10,3 12,5" />
      </svg>
    ),
  },
  {
    key: "dockBottom",
    title: "Dock Bottom",
    action: () => dockShapes("bottom"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="4" y="4" width="12" height="8" rx="1" />
        <rect x="6" y="13" width="8" height="5" rx="1" strokeDasharray="2,2" />
        <polyline points="10,14 10,17" />
        <polyline points="8,15 10,17 12,15" />
      </svg>
    ),
  },
  {
    key: "dockTopLeft",
    title: "Dock Top-Left",
    action: () => dockShapes("topLeft"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="8" y="8" width="10" height="10" rx="1" />
        <rect x="2" y="2" width="7" height="7" rx="1" strokeDasharray="2,2" />
        <polyline points="5,8 5,3 3,5" />
        <polyline points="8,5 3,5 5,3" />
      </svg>
    ),
  },
  {
    key: "dockBottomRight",
    title: "Dock Bottom-Right",
    action: () => dockShapes("bottomRight"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="10" height="10" rx="1" />
        <rect x="11" y="11" width="7" height="7" rx="1" strokeDasharray="2,2" />
        <polyline points="15,12 15,17 17,15" />
        <polyline points="12,15 17,15 15,17" />
      </svg>
    ),
  },
];

const SWAP_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "swapHorizontally",
    title: "Swap Horizontally",
    action: () => swapShapes("horizontal"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="6" width="5" height="8" rx="1" />
        <rect x="13" y="6" width="5" height="8" rx="1" />
        <line x1="7" y1="8" x2="13" y2="8" />
        <polyline points="11,6 13,8 11,10" />
        <line x1="13" y1="12" x2="7" y2="12" />
        <polyline points="9,10 7,12 9,14" />
      </svg>
    ),
  },
  {
    key: "swapVertically",
    title: "Swap Vertically",
    action: () => swapShapes("vertical"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="2" width="8" height="5" rx="1" />
        <rect x="6" y="13" width="8" height="5" rx="1" />
        <line x1="8" y1="7" x2="8" y2="13" />
        <polyline points="6,11 8,13 10,11" />
        <line x1="12" y1="13" x2="12" y2="7" />
        <polyline points="10,9 12,7 14,9" />
      </svg>
    ),
  },
  {
    key: "swapObjects",
    title: "Swap Objects",
    action: () => swapShapes("objects"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="6" height="6" rx="1" />
        <rect x="12" y="12" width="6" height="6" rx="1" />
        <path d="M9,5 L12,5 L12,11" fill="none" />
        <polyline points="10,3 12,5 10,7" />
        <path d="M11,15 L8,15 L8,9" fill="none" />
        <polyline points="10,17 8,15 10,13" />
      </svg>
    ),
  },
  {
    key: "swapText",
    title: "Swap Text",
    action: () => swapShapes("text"),
    icon: (
      <svg viewBox="0 0 20 20">
        <text x="3" y="9" fontSize="7" fill="currentColor" stroke="none">
          A
        </text>
        <text x="12" y="16" fontSize="7" fill="currentColor" stroke="none">
          B
        </text>
        <path d="M9,4 L14,4 L14,8" fill="none" />
        <polyline points="12,3 14,4 12,6" />
        <path d="M11,16 L6,16 L6,12" fill="none" />
        <polyline points="8,17 6,16 8,14" />
      </svg>
    ),
  },
  {
    key: "swapColor",
    title: "Swap Colours",
    action: () => swapShapes("color"),
    icon: (
      <svg viewBox="0 0 20 20">
        <circle cx="6" cy="7" r="4" />
        <circle cx="14" cy="13" r="4" strokeDasharray="2,2" />
        <path d="M9,5 L14,5 L14,9" fill="none" />
        <polyline points="12,4 14,5 12,7" />
        <path d="M11,15 L6,15 L6,11" fill="none" />
        <polyline points="8,16 6,15 8,13" />
      </svg>
    ),
  },
];

const LAYOUT_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "fitToSlide",
    title: "Fit to Slide",
    action: () => fitToSlide(true),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" strokeDasharray="2,1.5" />
        <rect x="6" y="7" width="8" height="6" rx="1" />
        <polyline points="4,4 6,4 6,6" fill="none" />
        <polyline points="16,4 14,4 14,6" fill="none" />
        <polyline points="4,16 6,16 6,14" fill="none" />
        <polyline points="16,16 14,16 14,14" fill="none" />
      </svg>
    ),
  },
  {
    key: "fitToSlideStretch",
    title: "Fit to Slide (Stretch)",
    action: () => fitToSlide(false),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" strokeDasharray="2,1.5" />
        <rect x="3" y="4" width="14" height="12" rx="1" />
        <polyline points="5,10 3,10 5,8" fill="none" />
        <polyline points="15,10 17,10 15,12" fill="none" />
      </svg>
    ),
  },
  {
    key: "smartAlign",
    title: "Smart Align",
    action: () => smartAlign(),
    // Scattered squares → arrow → aligned squares (matches smart-align.png).
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="1.5" y="3" width="4.5" height="4.5" rx="1" />
        <rect x="3" y="11.5" width="4.5" height="4.5" rx="1" />
        <line x1="8.5" y1="10" x2="12" y2="10" />
        <polyline points="10.5,8.5 12,10 10.5,11.5" fill="none" />
        <rect x="14.5" y="3" width="4.5" height="4.5" rx="1" />
        <rect x="14.5" y="11.5" width="4.5" height="4.5" rx="1" />
      </svg>
    ),
  },
  {
    key: "multiplyObject",
    title: "Multiply",
    dialog: "multiply",
    action: async () => {}, // opens the config dialog; see `dialog`
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="6" height="6" rx="1" />
        <rect x="12" y="2" width="6" height="6" rx="1" strokeDasharray="1.8,1.6" fill="none" />
        <rect x="2" y="12" width="6" height="6" rx="1" strokeDasharray="1.8,1.6" fill="none" />
        <rect x="12" y="12" width="6" height="6" rx="1" strokeDasharray="1.8,1.6" fill="none" />
      </svg>
    ),
  },
  {
    key: "sliceObject",
    title: "Slice",
    dialog: "slice",
    action: async () => {}, // opens the config dialog; see `dialog`
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" />
        <line x1="7.3" y1="2" x2="7.3" y2="18" strokeDasharray="1.6,1.4" />
        <line x1="12.6" y1="2" x2="12.6" y2="18" strokeDasharray="1.6,1.4" />
        <line x1="2" y1="7.3" x2="18" y2="7.3" strokeDasharray="1.6,1.4" />
        <line x1="2" y1="12.6" x2="18" y2="12.6" strokeDasharray="1.6,1.4" />
      </svg>
    ),
  },
  {
    key: "alignCircle",
    title: "Align Circle",
    circlePanel: true,
    action: async () => {}, // arranges + opens the Align Circle panel; see `circlePanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <circle cx="10" cy="10" r="7" strokeDasharray="2,2" />
        <circle cx="10" cy="3" r="1.5" />
        <circle cx="16" cy="8" r="1.5" />
        <circle cx="14" cy="16" r="1.5" />
        <circle cx="6" cy="16" r="1.5" />
        <circle cx="4" cy="8" r="1.5" />
      </svg>
    ),
  },
];

const SIZE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "sameWidth",
    title: "Same Width",
    action: () => resizeShapes("width"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="4" width="14" height="5" rx="1" />
        <rect x="3" y="11" width="14" height="5" rx="1" />
        <line x1="3" y1="2" x2="3" y2="4" opacity="0.5" />
        <line x1="17" y1="2" x2="17" y2="4" opacity="0.5" />
        <line x1="3" y1="2" x2="17" y2="2" opacity="0.5" />
      </svg>
    ),
  },
  {
    key: "sameHeight",
    title: "Same Height",
    action: () => resizeShapes("height"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="4" y="3" width="5" height="14" rx="1" />
        <rect x="11" y="3" width="5" height="14" rx="1" />
        <line x1="2" y1="3" x2="4" y2="3" opacity="0.5" />
        <line x1="2" y1="17" x2="4" y2="17" opacity="0.5" />
        <line x1="2" y1="3" x2="2" y2="17" opacity="0.5" />
      </svg>
    ),
  },
  {
    key: "sameWidthLeft",
    title: "Same Width (Anchor Left)",
    action: () => resizeShapes("widthLeft"),
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="3" y1="2" x2="3" y2="18" />
        <rect x="3" y="4" width="14" height="5" rx="1" />
        <rect x="3" y="11" width="14" height="5" rx="1" />
      </svg>
    ),
  },
  {
    key: "sameHeightTop",
    title: "Same Height (Anchor Top)",
    action: () => resizeShapes("heightTop"),
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="2" y1="3" x2="18" y2="3" />
        <rect x="4" y="3" width="5" height="14" rx="1" />
        <rect x="11" y="3" width="5" height="14" rx="1" />
      </svg>
    ),
  },
  {
    key: "sameSize",
    title: "Same Size",
    action: () => resizeShapes("size"),
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="6" rx="1" />
        <rect x="3" y="11" width="14" height="6" rx="1" />
      </svg>
    ),
  },
];

function stretchBtn(op: StretchOp, title: string, icon: ReactNode): ToolButton {
  return { key: `stretch-${op}`, cmdAction: op, title, icon, action: () => stretchShapes(op) };
}

const STRETCH_BUTTONS: ReadonlyArray<ToolButton> = [
  stretchBtn(
    "stretchLeft",
    "Stretch Left",
    <svg viewBox="0 0 20 20">
      <line x1="3" y1="2" x2="3" y2="18" />
      <rect x="8" y="6" width="9" height="8" rx="1" />
      <line x1="8" y1="10" x2="4" y2="10" />
      <polyline points="6,8 4,10 6,12" />
    </svg>,
  ),
  stretchBtn(
    "stretchRight",
    "Stretch Right",
    <svg viewBox="0 0 20 20">
      <line x1="17" y1="2" x2="17" y2="18" />
      <rect x="3" y="6" width="9" height="8" rx="1" />
      <line x1="12" y1="10" x2="16" y2="10" />
      <polyline points="14,8 16,10 14,12" />
    </svg>,
  ),
  stretchBtn(
    "stretchUp",
    "Stretch Up",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="3" x2="18" y2="3" />
      <rect x="6" y="8" width="8" height="9" rx="1" />
      <line x1="10" y1="8" x2="10" y2="4" />
      <polyline points="8,6 10,4 12,6" />
    </svg>,
  ),
  stretchBtn(
    "stretchDown",
    "Stretch Down",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="17" x2="18" y2="17" />
      <rect x="6" y="3" width="8" height="9" rx="1" />
      <line x1="10" y1="12" x2="10" y2="16" />
      <polyline points="8,14 10,16 12,14" />
    </svg>,
  ),
  stretchBtn(
    "pullLeft",
    "Pull Left",
    <svg viewBox="0 0 20 20">
      <line x1="3" y1="2" x2="3" y2="18" />
      <rect x="3" y="6" width="14" height="8" rx="1" />
      <line x1="8" y1="10" x2="5" y2="10" />
      <polyline points="7,8 5,10 7,12" />
    </svg>,
  ),
  stretchBtn(
    "pullRight",
    "Pull Right",
    <svg viewBox="0 0 20 20">
      <line x1="17" y1="2" x2="17" y2="18" />
      <rect x="3" y="6" width="14" height="8" rx="1" />
      <line x1="12" y1="10" x2="15" y2="10" />
      <polyline points="13,8 15,10 13,12" />
    </svg>,
  ),
  stretchBtn(
    "edgeMatchTop",
    "Edge Match Top",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="3" x2="18" y2="3" />
      <rect x="4" y="3" width="5" height="10" rx="1" />
      <rect x="11" y="3" width="5" height="14" rx="1" />
    </svg>,
  ),
  stretchBtn(
    "edgeMatchBottom",
    "Edge Match Bottom",
    <svg viewBox="0 0 20 20">
      <line x1="2" y1="17" x2="18" y2="17" />
      <rect x="4" y="7" width="5" height="10" rx="1" />
      <rect x="11" y="3" width="5" height="14" rx="1" />
    </svg>,
  ),
];

const TABLE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "tableToTextExplode",
    title: "Table to Text (Explode)",
    action: tableToTextExplode,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" />
        <line x1="10" y1="2" x2="10" y2="18" />
        <line x1="2" y1="10" x2="18" y2="10" />
        <polyline points="14,6 17,6 17,3" fill="none" />
      </svg>
    ),
  },
  {
    key: "tableToTextAsIs",
    title: "Table to Text (As-Is)",
    action: tableToTextAsIs,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" />
        <line x1="10" y1="2" x2="10" y2="18" />
        <line x1="2" y1="10" x2="18" y2="10" />
        <line x1="13" y1="5" x2="17" y2="5" />
        <line x1="13" y1="8" x2="17" y2="8" />
      </svg>
    ),
  },
  {
    key: "splitTable",
    title: "Split Table",
    tablePanel: true,
    action: async () => {}, // opens the Table Split panel; see `tablePanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="7" rx="1" fill="none" />
        <rect x="2" y="11" width="16" height="7" rx="1" fill="none" />
        <line x1="10" y1="2" x2="10" y2="9" />
        <line x1="10" y1="11" x2="10" y2="18" />
        <polyline points="7,13 10,11 13,13" fill="none" />
      </svg>
    ),
  },
  {
    key: "alignShapesAsTable",
    title: "Align Shapes as Table",
    alignPanel: true,
    action: async () => {}, // opens the Align-in-Table panel; see `alignPanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="7" height="7" rx="1" fill="none" />
        <rect x="11" y="2" width="7" height="7" rx="1" fill="none" />
        <rect x="2" y="11" width="7" height="7" rx="1" fill="none" />
        <rect x="11" y="11" width="7" height="7" rx="1" fill="none" />
      </svg>
    ),
  },
];

const TEXT_FORMAT_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "deleteAllText",
    title: "Delete All Text",
    action: deleteAllText,
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="3" y1="5" x2="13" y2="5" />
        <line x1="3" y1="10" x2="10" y2="10" />
        <line x1="3" y1="15" x2="12" y2="15" />
        <line x1="14" y1="7" x2="18" y2="17" />
        <line x1="18" y1="7" x2="14" y2="17" />
      </svg>
    ),
  },
  {
    key: "splitText",
    title: "Split Text",
    action: splitTextByParagraphs,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="2" width="14" height="5" rx="1" fill="none" />
        <rect x="3" y="9" width="14" height="4" rx="1" fill="none" strokeDasharray="1.8,1.4" />
        <rect x="3" y="14" width="14" height="4" rx="1" fill="none" strokeDasharray="1.8,1.4" />
      </svg>
    ),
  },
  {
    key: "mergeBoxes",
    title: "Merge Text Box",
    action: mergeTextBoxes,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="6" height="5" rx="1" fill="none" />
        <rect x="11" y="3" width="6" height="5" rx="1" fill="none" />
        <rect x="5" y="12" width="10" height="5" rx="1" fill="none" />
        <line x1="10" y1="8" x2="10" y2="12" />
      </svg>
    ),
  },
  {
    key: "sortAscending",
    title: "Sort A–Z",
    action: sortTextAscending,
    icon: (
      <svg viewBox="0 0 20 20">
        <text x="2" y="8" fontSize="7" stroke="none" fill="currentColor">A</text>
        <text x="2" y="17" fontSize="7" stroke="none" fill="currentColor">Z</text>
        <polyline points="14,4 14,16" fill="none" />
        <polyline points="11,13 14,16 17,13" fill="none" />
      </svg>
    ),
  },
  {
    key: "sortDescending",
    title: "Sort Z–A",
    action: sortTextDescending,
    icon: (
      <svg viewBox="0 0 20 20">
        <text x="2" y="8" fontSize="7" stroke="none" fill="currentColor">Z</text>
        <text x="2" y="17" fontSize="7" stroke="none" fill="currentColor">A</text>
        <polyline points="14,4 14,16" fill="none" />
        <polyline points="11,7 14,4 17,7" fill="none" />
      </svg>
    ),
  },
  {
    key: "removeLineBreaks",
    title: "Remove Line Breaks",
    action: removeLineBreaks,
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="3" y1="6" x2="17" y2="6" />
        <path d="M3 12 H14 a2 2 0 0 0 0-4" fill="none" />
        <polyline points="6,9 3,12 6,15" fill="none" />
      </svg>
    ),
  },
  {
    key: "reorderShapes",
    title: "Reorder by Text",
    action: reorderShapesByText, // applies the default "both" reading order
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="4" rx="1" fill="none" />
        <rect x="3" y="13" width="14" height="4" rx="1" fill="none" />
        <polyline points="10,8 10,12" fill="none" />
        <polyline points="8,10 10,12 12,10" fill="none" />
      </svg>
    ),
  },
  {
    key: "wrapText",
    title: "Wrap Text",
    action: toggleWrapText,
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="3" y1="5" x2="17" y2="5" />
        <path d="M3 10 H14 a2.5 2.5 0 0 1 0 5 H11" fill="none" />
        <polyline points="13,13 11,15 13,17" fill="none" />
        <line x1="3" y1="15" x2="8" y2="15" />
      </svg>
    ),
  },
  {
    key: "singleLineSpacing",
    title: "Single Line Spacing",
    action: applySingleLineSpacing,
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="7" y1="4" x2="17" y2="4" />
        <line x1="7" y1="8" x2="17" y2="8" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="7" y1="16" x2="17" y2="16" />
        <polyline points="2,7 4,4 6,7" fill="none" />
        <polyline points="2,13 4,16 6,13" fill="none" />
        <line x1="4" y1="4" x2="4" y2="16" />
      </svg>
    ),
  },
  {
    key: "replaceFont",
    title: "Replace Font",
    textPanel: "replaceFont",
    action: async () => {}, // opens the Replace Font panel; see `textPanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <text x="2" y="15" fontSize="12" stroke="none" fill="currentColor">F</text>
        <polyline points="10,10 13,10" fill="none" />
        <polyline points="11.5,8.5 13,10 11.5,11.5" fill="none" />
        <text x="13" y="15" fontSize="9" stroke="none" fill="currentColor">f</text>
      </svg>
    ),
  },
  {
    key: "connectorWeight",
    title: "Line Weight",
    textPanel: "connectorWeight",
    action: async () => {}, // opens the Line Weight panel; see `textPanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="3" y1="5" x2="17" y2="5" strokeWidth="1" />
        <line x1="3" y1="10" x2="17" y2="10" strokeWidth="2" />
        <line x1="3" y1="15" x2="17" y2="15" strokeWidth="3.5" />
      </svg>
    ),
  },
];

/*
 * MARGIN + COPY STYLE — ported from the VSTO plugin's NewDeckTools "MARGIN"
 * and Copy/Apply Formatting sections (see marginOps.ts / copyFormatOps.ts for
 * the behaviour notes and the places this port deliberately improves on VSTO).
 */

/** Edge segments used to highlight which margins a preset zeroes. */
const MARGIN_EDGE_SEGMENTS = {
  l: [4, 3, 4, 17],
  t: [3, 4, 17, 4],
  r: [16, 3, 16, 17],
  b: [3, 16, 17, 16],
} as const;

type MarginEdgeKey = keyof typeof MARGIN_EDGE_SEGMENTS;

/** A text box outline with the zeroed edges drawn heavy. */
function marginIcon(edges: ReadonlyArray<MarginEdgeKey>): ReactNode {
  return (
    <svg viewBox="0 0 20 20">
      <rect x="2" y="2" width="16" height="16" rx="1" fill="none" />
      {edges.map((edge) => {
        const [x1, y1, x2, y2] = MARGIN_EDGE_SEGMENTS[edge];
        return <line key={edge} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="2.5" />;
      })}
    </svg>
  );
}

function marginBtn(preset: MarginPreset, title: string, edges: ReadonlyArray<MarginEdgeKey>): ToolButton {
  return {
    key: `margin${preset.charAt(0).toUpperCase()}${preset.slice(1)}`,
    title,
    icon: marginIcon(edges),
    action: () => zeroMargins(preset),
  };
}

const MARGIN_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "setMargin",
    title: "Set Margin",
    marginPanel: true,
    action: async () => {}, // opens the Set Margin panel; see `marginPanel`
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="2" y="2" width="16" height="16" rx="1" fill="none" />
        <rect x="6" y="6" width="8" height="8" rx="1" fill="none" strokeDasharray="2,2" />
      </svg>
    ),
  },
  marginBtn("all", "0-Margin All", ["l", "t", "r", "b"]),
  marginBtn("left", "0-Margin Left", ["l"]),
  marginBtn("right", "0-Margin Right", ["r"]),
  marginBtn("top", "0-Margin Top", ["t"]),
  marginBtn("bottom", "0-Margin Bottom", ["b"]),
  marginBtn("leftRight", "0-Margin L & R", ["l", "r"]),
  marginBtn("topBottom", "0-Margin T & B", ["t", "b"]),
  marginBtn("topLeft", "0-Margin T & L", ["t", "l"]),
  marginBtn("topRight", "0-Margin T & R", ["t", "r"]),
  marginBtn("bottomLeft", "0-Margin B & L", ["b", "l"]),
  marginBtn("bottomRight", "0-Margin B & R", ["b", "r"]),
];

const COPY_STYLE_BUTTONS: ReadonlyArray<ToolButton> = [
  {
    key: "copyStyle",
    title: "Copy Style",
    action: async () => {
      await copyStyle();
    },
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="5" width="9" height="11" rx="1" fill="none" />
        <rect x="7" y="2" width="9" height="11" rx="1" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyWidth",
    title: "Apply Width",
    action: applyWidth,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="6" width="14" height="8" rx="1" fill="none" />
        <polyline points="5,4 3,4 3,16 5,16" fill="none" />
        <polyline points="15,4 17,4 17,16 15,16" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyHeight",
    title: "Apply Height",
    action: applyHeight,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="6" y="3" width="8" height="14" rx="1" fill="none" />
        <polyline points="4,5 4,3 16,3 16,5" fill="none" />
        <polyline points="4,15 4,17 16,17 16,15" fill="none" />
      </svg>
    ),
  },
  {
    key: "applySize",
    title: "Apply Size",
    action: applySize,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="14" rx="1" fill="none" />
        <polyline points="7,13 13,13 13,7" fill="none" />
        <line x1="7" y1="13" x2="13" y2="7" />
      </svg>
    ),
  },
  {
    key: "applyPosition",
    title: "Apply Position",
    action: applyPosition,
    icon: (
      <svg viewBox="0 0 20 20">
        <line x1="10" y1="2" x2="10" y2="18" strokeDasharray="2,2" />
        <line x1="2" y1="10" x2="18" y2="10" strokeDasharray="2,2" />
        <rect x="7" y="7" width="6" height="6" rx="1" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyShapeStyle",
    title: "Apply Shape Style",
    action: applyShapeStyle,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="4" width="6" height="12" rx="1" fill="none" />
        <rect x="11" y="4" width="6" height="12" rx="1" fill="none" strokeDasharray="2,2" />
        <polyline points="8,10 12,10" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyTextFormat",
    title: "Apply Text Format",
    action: applyTextFormat,
    icon: (
      <svg viewBox="0 0 20 20">
        <text x="2" y="15" fontSize="13" stroke="none" fill="currentColor">A</text>
        <line x1="12" y1="6" x2="18" y2="6" />
        <line x1="12" y1="10" x2="18" y2="10" />
        <line x1="12" y1="14" x2="16" y2="14" />
      </svg>
    ),
  },
  {
    key: "applyColor",
    title: "Apply Color",
    action: applyFillColor,
    icon: (
      <svg viewBox="0 0 20 20">
        <path d="M4 12 L11 5 l4 4 -7 7 z" fill="none" />
        <line x1="8" y1="3" x2="13" y2="8" />
        <path d="M16 13 c1.5 2 2 2.7 2 3.5 a2 2 0 0 1 -4 0 c0 -0.8 0.5 -1.5 2 -3.5 z" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyOutline",
    title: "Apply Outline",
    action: applyOutline,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="14" rx="1" fill="none" strokeWidth="2.5" />
      </svg>
    ),
  },
  {
    key: "applyProportions",
    title: "Apply Proportions",
    action: applyProportions,
    icon: (
      <svg viewBox="0 0 20 20">
        <path d="M3 15 L7 5 h6 l4 10 z" fill="none" />
        <circle cx="7" cy="5" r="1.6" fill="none" />
        <circle cx="13" cy="5" r="1.6" fill="none" />
      </svg>
    ),
  },
  {
    key: "applyAllFormatting",
    title: "Apply All Formatting",
    action: applyAllFormatting,
    icon: (
      <svg viewBox="0 0 20 20">
        <rect x="3" y="3" width="14" height="14" rx="1" fill="none" />
        <polyline points="6,10 9,13 14,7" fill="none" />
      </svg>
    ),
  },
];

interface ToolGroup {
  readonly name: string;
  readonly buttons: ReadonlyArray<ToolButton>;
}

// How long the success toast stays on screen before it fades out on its own.
const TOAST_AUTO_HIDE_MS = 2600;

// Category tabs, in the order they're shown across the top of the panel.
// "Tables & Text" merges the old separate Table/Text Format groups into one
// tab — both still carry every button + action from their original arrays.
const TOOL_GROUPS: ReadonlyArray<ToolGroup> = [
  { name: "Layout", buttons: LAYOUT_BUTTONS },
  { name: "Alignment", buttons: ALIGN_BUTTONS },
  { name: "Distribute", buttons: DISTRIBUTE_BUTTONS },
  { name: "Size", buttons: SIZE_BUTTONS },
  { name: "Stretch", buttons: STRETCH_BUTTONS },
  { name: "Duplicate", buttons: DUPLICATE_BUTTONS },
  { name: "Dock", buttons: DOCK_BUTTONS },
  { name: "Arrange", buttons: ARRANGE_BUTTONS },
  { name: "Swap", buttons: SWAP_BUTTONS },
  { name: "Margins", buttons: MARGIN_BUTTONS },
  { name: "Copy Style", buttons: COPY_STYLE_BUTTONS },
  { name: "Tables & Text", buttons: [...TABLE_BUTTONS, ...TEXT_FORMAT_BUTTONS] },
];

/** "Rectangle 4" / "3 shapes" — what the Copy Style hint calls the picked source. */
function describeCopiedSource(style: CopiedStyle): string {
  if (style.shapes.length !== 1) return `${style.shapes.length} shapes`;
  return style.sourceName || "1 shape";
}

/** Slug a tab/group name into a stable id fragment for aria-controls/id pairs. */
const slug = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

interface SearchCommand extends ToolButton {
  group: string;
}

// Flat command index for the search palette — built from TOOL_GROUPS so it
// always reflects exactly the actions + categories the panel offers.
const SEARCH_COMMANDS: ReadonlyArray<SearchCommand> = TOOL_GROUPS.flatMap((g) =>
  g.buttons.map((b) => ({ ...b, group: g.name })),
);

/** Match every whitespace-separated term against the title + group. */
function filterCommands(query: string): ReadonlyArray<SearchCommand> {
  const q = query.toLowerCase().trim();
  if (!q) return SEARCH_COMMANDS;
  const terms = q.split(/\s+/);
  return SEARCH_COMMANDS.filter((cmd) => {
    const haystack = `${cmd.title} ${cmd.group}`.toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });
}

/** Wrap matched query terms in <mark> for the dropdown label. */
function highlight(text: string, query: string): ReactNode {
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return text;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`));
  const parts = text.split(new RegExp(`(${escaped.join("|")})`, "gi"));
  // Key by the part's cumulative character offset (content-derived + stable),
  // not the bare array index.
  let offset = 0;
  return parts.map((part) => {
    const key = `${offset}-${part}`;
    offset += part.length;
    return terms.includes(part.toLowerCase()) ? (
      <mark key={key}>{part}</mark>
    ) : (
      <span key={key}>{part}</span>
    );
  });
}

/**
 * Format Tools feature panel.
 *
 * Tools are grouped into the TOOL_GROUPS category tabs (Layout, Alignment,
 * Distribute, Size, Stretch, Duplicate, Dock, Arrange, Swap, Tables & Text).
 * Every group's button grid stays mounted (hidden, not unmounted) regardless
 * of the active tab, so the command palette's dom-click lookup
 * (`[data-cmd-action="format-<action>"]`) can always find its target — see
 * `activate`'s `group` param, which switches the visible tab to match
 * whichever button a search/palette command just ran. The last selected
 * shape is the reference for align and cascade operations. Layout's
 * Multiply + Slice open a config dialog (rows/cols/gutters) before running
 * their OOXML clone.
 */
interface FormatToolsProps {
  readonly onBack?: () => void;
}

export function FormatTools({ onBack }: Readonly<FormatToolsProps>) {
  const tools = useFormatTools();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [activeTab, setActiveTab] = useState<string>(TOOL_GROUPS[0]!.name);
  const [gridMode, setGridMode] = useState<GridMode | null>(null);
  const [textPanel, setTextPanel] = useState<TextPanelMode | null>(null);
  const [circle, setCircle] = useState<CircleState | null>(null);
  const [tableSplit, setTableSplit] = useState(false);
  const [alignInTable, setAlignInTable] = useState(false);
  const [marginPanel, setMarginPanel] = useState(false);
  // The picked style lives in copyFormatOps (module scope) so it survives this
  // panel unmounting; mirrored into state purely to render the "Copied …" hint.
  const [copied, setCopied] = useState<CopiedStyle | null>(() => getCopiedStyle());
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [toastOn, setToastOn] = useState(false);
  // Original geometry captured when the Align panel opens, so Cancel can revert
  // the live preview.
  const alignOriginals = useRef<ShapeGeomSnapshot[]>([]);
  // Serialises live-preview applies (latest wins) so overlapping PowerPoint.run
  // calls can't collide while the user switches position quickly.
  const previewCfg = useRef<AlignInTableConfig | null>(null);
  const previewRunning = useRef(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => filterCommands(query), [query]);

  useEffect(() => onCopiedStyleChange(setCopied), []);

  // Close the dropdown when clicking outside the search wrapper.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Surface each successful action as a transient toast (mockup's "snack"),
  // instead of a static highlighted line — auto-hides itself so it never
  // lingers over the tool grid.
  useEffect(() => {
    if (!tools.status || tools.busy) return;
    setToastMsg(tools.status);
    setToastOn(true);
    const hideTimer = setTimeout(() => setToastOn(false), TOAST_AUTO_HIDE_MS);
    return () => clearTimeout(hideTimer);
  }, [tools.status, tools.busy]);

  // `group` switches the visible tab to match whichever button just ran —
  // needed because every group's buttons stay mounted (hidden, not removed)
  // so the command palette's dom-click can always find them (see the tabs'
  // `hidden` prop below); without this, running a command from the search
  // box or the palette while a different tab is showing would silently
  // apply the change behind a tab the user isn't looking at.
  const activate = (cmd: ToolButton, group?: string) => {
    if (group) setActiveTab(group);
    setTextPanel(null);
    setCircle(null);
    setTableSplit(false);
    setAlignInTable(false);
    setMarginPanel(false);

    if (cmd.marginPanel) {
      setMarginPanel(true);
      return;
    }
    if (cmd.dialog) {
      setGridMode(cmd.dialog);
      return;
    }
    if (cmd.textPanel) {
      setTextPanel(cmd.textPanel);
      return;
    }
    if (cmd.circlePanel) {
      tools.run(cmd.title, async () => {
        setCircle(await alignCircle());
      });
      return;
    }
    if (cmd.tablePanel) {
      setTableSplit(true);
      return;
    }
    if (cmd.alignPanel) {
      // Snapshot the current geometry first so Cancel can revert the live
      // preview, then open the panel.
      void captureSelectionGeometry()
        .then((orig) => {
          alignOriginals.current = orig;
        })
        .catch(() => {
          alignOriginals.current = [];
        })
        .finally(() => setAlignInTable(true));
      return;
    }
    tools.run(cmd.title, cmd.action);
  };

  // Apply an Align-in-Table config live; serialised so rapid position switches
  // never overlap (only the most recent config is applied while one is running).
  const previewAlign = (cfg: AlignInTableConfig): void => {
    previewCfg.current = cfg;
    if (previewRunning.current) return;
    previewRunning.current = true;
    const pump = (): void => {
      const next = previewCfg.current;
      previewCfg.current = null;
      if (!next) {
        previewRunning.current = false;
        return;
      }
      void tools.run("Align Shapes as Table", () => alignShapesInTable(next)).then(pump);
    };
    pump();
  };

  const runCommand = (cmd: SearchCommand) => {
    setOpen(false);
    setQuery("");
    setActiveIdx(-1);
    activate(cmd, cmd.group);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = matches[activeIdx] ?? matches[0];
      if (cmd) runCommand(cmd);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const renderGrid = (buttons: ReadonlyArray<ToolButton>, group: string) => (
    <div className={styles.cardGrid}>
      {buttons.map((b) => (
        <button
          key={b.key}
          type="button"
          className={styles.card}
          title={b.title}
          data-cmd-action={`format-${b.cmdAction ?? b.key}`}
          disabled={tools.busy}
          onClick={() => activate(b, group)}
        >
          <span className={styles.cardIcon}>{b.icon}</span>
          <span className={styles.cardLabel}>{b.title}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Format Tools" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.refInfo}>Reference = last selected shape</div>
      {copied && (
        <div className={styles.copiedInfo}>
          <span>{`Style copied from ${describeCopiedSource(copied)} — select targets, then Apply…`}</span>
          <button type="button" className={styles.linkBtn} onClick={clearCopiedStyle}>
            Clear
          </button>
        </div>
      )}

      <div className={styles.searchWrapper} ref={searchRef}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search actions... e.g. align, distribute, swap"
          autoComplete="off"
          aria-label="Search actions"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onSearchKeyDown}
        />
        {open && (
          <div className={styles.searchDropdown}>
            {matches.length === 0 ? (
              <div className={styles.searchEmpty}>No actions found</div>
            ) : (
              matches.map((cmd, i) => (
                <button
                  key={cmd.key}
                  type="button"
                  className={`${styles.searchItem} ${i === activeIdx ? styles.searchItemActive : ""}`}
                  // onMouseDown (not onClick) so it fires before the input blur.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    runCommand(cmd);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className={styles.searchItemGroup}>{cmd.group}</span>
                  <span className={styles.searchItemLabel}>{highlight(cmd.title, query)}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {tools.error && <div className={`${styles.status} ${styles.error}`}>{tools.error}</div>}

      <div className={styles.tabs} role="tablist" aria-label="Format tool categories">
        {TOOL_GROUPS.map((g) => {
          const active = activeTab === g.name;
          return (
            <button
              key={g.name}
              type="button"
              role="tab"
              id={`fmt-tab-${slug(g.name)}`}
              aria-selected={active}
              aria-controls={`fmt-panel-${slug(g.name)}`}
              className={`${styles.tab} ${active ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(g.name)}
            >
              {g.name}
            </button>
          );
        })}
      </div>

      {TOOL_GROUPS.map((g) => (
        <div
          key={g.name}
          id={`fmt-panel-${slug(g.name)}`}
          role="tabpanel"
          aria-labelledby={`fmt-tab-${slug(g.name)}`}
          hidden={activeTab !== g.name}
          className={styles.tabPanel}
        >
          {renderGrid(g.buttons, g.name)}
        </div>
      ))}

      {gridMode && (
        <GridConfigDialog
          mode={gridMode}
          busy={tools.busy}
          onCancel={() => setGridMode(null)}
          onApply={(cfg) => {
            const mode = gridMode;
            setGridMode(null);
            tools.run(mode === "multiply" ? "Multiply" : "Slice", () =>
              mode === "multiply" ? multiplyObject(cfg) : sliceObject(cfg),
            );
          }}
        />
      )}

      {circle && (
        <AlignCirclePanel
          state={circle}
          busy={tools.busy}
          onClose={() => setCircle(null)}
          onRadius={(delta) => {
            const next = { ...circle, radius: Math.max(20, circle.radius + delta) };
            setCircle(next);
            tools.run("Align Circle", () => relayoutCircle(next));
          }}
          onAngle={(delta) => {
            const next = { ...circle, angleDeg: (((circle.angleDeg + delta) % 360) + 360) % 360 };
            setCircle(next);
            tools.run("Align Circle", () => relayoutCircle(next));
          }}
          onSize={(delta) => {
            const next = { ...circle, size: Math.max(10, circle.size + delta) };
            setCircle(next);
            tools.run("Align Circle", () => relayoutCircle(next));
          }}
          onDirection={(direction) => {
            const next = { ...circle, direction };
            setCircle(next);
            tools.run("Align Circle", () => relayoutCircle(next));
          }}
          onArc={(arc) => {
            const next = { ...circle, arc };
            setCircle(next);
            tools.run("Align Circle", () => relayoutCircle(next));
          }}
          onAddShape={() => {
            // Unlike the nudges above, the next state here (the new shape's
            // id) isn't known until the duplicate + relayout finish, so it's
            // read back from addCircleShape's result instead of being
            // computed up front.
            tools.run("Align Circle", async () => {
              setCircle(await addCircleShape(circle));
            });
          }}
          onRemoveShape={() => {
            tools.run("Align Circle", async () => {
              setCircle(await removeCircleShape(circle));
            });
          }}
        />
      )}

      {tableSplit && (
        <TableSplitPanel
          busy={tools.busy}
          onCancel={() => setTableSplit(false)}
          onApply={(headerRows, splitRow) => {
            setTableSplit(false);
            tools.run("Split Table", () => splitTable(headerRows, splitRow));
          }}
        />
      )}

      {alignInTable && (
        <AlignInTablePanel
          busy={tools.busy}
          onChange={previewAlign}
          onOk={() => setAlignInTable(false)}
          onCancel={() => {
            setAlignInTable(false);
            const orig = alignOriginals.current;
            if (orig.length > 0) void restoreShapeGeometry(orig);
          }}
        />
      )}

      {marginPanel && (
        <MarginConfigPanel
          busy={tools.busy}
          onCancel={() => setMarginPanel(false)}
          onApply={(margins) => {
            setMarginPanel(false);
            tools.run("Set Margin", () => setTextMargins(margins));
          }}
        />
      )}

      {textPanel && (
        <TextConfigPanel
          mode={textPanel}
          busy={tools.busy}
          onCancel={() => setTextPanel(null)}
          onApplyFont={(fromFont, toFont, allSlides) => {
            setTextPanel(null);
            tools.run("Replace Font", async () => {
              await replaceFont(fromFont, toFont, allSlides);
            });
          }}
          onApplyWeight={(weight) => {
            setTextPanel(null);
            tools.run("Line Weight", () => setConnectorWeight(weight));
          }}
        />
      )}

      <div
        className={`${styles.toast} ${toastOn ? styles.toastOn : ""}`}
        role="status"
        aria-live="polite"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          width="1em"
          height="1em"
          className={styles.toastIcon}
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M7 10l2 2 4-4" />
        </svg>
        <span>{toastMsg ?? ""}</span>
      </div>
    </div>
  );
}
